import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AlertTriangle } from 'lucide-react'
import { ETICHETA_STARE_TASK, type StareTask } from '@b4l/shared'
import { api } from '../lib/api'
import { Modal } from './ui/Modal'
import { useDubluraStore } from '../stores/dublura'

const zi = (iso: string) =>
  new Date(iso).toLocaleDateString('ro-RO', { day: 'numeric', month: 'short' })

/**
 * „Există deja o sarcină asemănătoare” (Madalin, 27.10.2026). Fara „creeaza oricum”: informatia se
 * adauga pe task-ul existent. Pe unul vazut — „Mergi la task”; pe unul din alt departament, pe
 * care omul nu-l vede — „Adaugă informația acolo”, ca un comentariu, fara sa i se arate continutul.
 */
export function FereastraDublura() {
  const { dublura: d, mesaj, text, inchide } = useDubluraStore()
  const navigate = useNavigate()
  const [scris, setScris] = useState('')
  const [trimite, setTrimite] = useState(false)
  // Fereastra noua: textul scris data trecuta nu ramane in ea.
  useEffect(() => setScris(''), [d?.id])
  /*
    Esc inchide doar fereastra asta, nu si formularul de sub ea (cu tot ce s-a scris): ascultatorul
    pe faza de captura vine inaintea celor puse de ferestre si opreste tasta acolo.
  */
  useEffect(() => {
    if (!d) return
    const k = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.stopImmediatePropagation()
      inchide()
    }
    window.addEventListener('keydown', k, true)
    return () => window.removeEventListener('keydown', k, true)
  }, [d, inchide])
  if (!d) return null

  const mergi = () => {
    inchide()
    // O navigare completa: inchide si formularul deschis sub fereastra.
    window.location.assign(`/task-uri?view=${d.id}`)
  }
  const adauga = async () => {
    const corp = (scris || text).trim()
    if (!corp) return
    setTrimite(true)
    try {
      await api.post(`/tasks/${d.id}/informatie`, { text: corp })
      toast.success(`Informația a ajuns la ${d.departamentNume ?? 'celălalt departament'}.`)
      inchide()
      navigate(0)
    } catch {
      toast.error('Nu s-a putut adăuga informația.')
    } finally {
      setTrimite(false)
    }
  }

  return (
    <Modal open onClose={inchide} title="Există deja o sarcină asemănătoare">
      <div className="grid gap-3">
        <p className="flex gap-2 rounded-lg bg-b4l-amber-light px-3 py-2 text-corp text-b4l-amber-dark">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{mesaj || 'Există deja o sarcină asemănătoare.'}</span>
        </p>
        {d.vizibil ? (
          <div className="grid gap-0.5 rounded-lg border border-b4l-border px-3 py-2">
            <b className="text-corp text-b4l-text [overflow-wrap:anywhere]">„{d.titlu}”</b>
            <span className="text-mic text-b4l-text-muted">
              {[
                d.departamentNume,
                d.stare ? (ETICHETA_STARE_TASK[d.stare as StareTask] ?? d.stare) : null,
                d.oameni.length ? `la ${d.oameni.join(', ')}` : null,
                `din ${zi(d.creat)}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        ) : (
          <label className="grid gap-1 text-mic text-b4l-text-muted">
            Ce vrei să adaugi (ajunge ca un comentariu pe task-ul lor, deschis din {zi(d.creat)})
            <textarea
              rows={4}
              defaultValue={text}
              onChange={(e) => setScris(e.target.value)}
              className="rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text"
            />
          </label>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={inchide}
            className="rounded-caseta border border-b4l-border px-3 py-1.5 text-corp"
          >
            Închide
          </button>
          {d.vizibil ? (
            <button
              type="button"
              onClick={mergi}
              className="rounded-caseta bg-b4l-blue px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90"
            >
              Mergi la task
            </button>
          ) : (
            <button
              type="button"
              disabled={trimite}
              onClick={adauga}
              className="rounded-caseta bg-b4l-blue px-3.5 py-1.5 text-corp font-semibold text-white hover:opacity-90 disabled:opacity-50"
            >
              Adaugă informația acolo
            </button>
          )}
        </div>
      </div>
    </Modal>
  )
}
